import { FEATURE_IMAGE_GUIDELINES_ID } from "@faire/help-center-utils/articleIds";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/www/brand-portal/my-shop/products";
import supportArticle from "@faire/web-api--source/routes/www/support/articles/articleId";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const ManageProductImagesIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="To update a product’s images, follow these steps:"
          description={{
            text: "Introduction to managing product images",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to your <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "First step to manage product images",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select the product you want to edit, then scroll down to the <BoldFormatting>Images</BoldFormatting> section."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Second step to manage product images",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="In the <BoldFormatting>Images</BoldFormatting> section, you can:"
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Introduction to image management options",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Nominate your featured image"
              description={{
                text: "Option to nominate featured image",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Rearrange the order that images appear on the product page"
              description={{
                text: "Option to rearrange images",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Reposition and resize image crops"
              description={{
                text: "Option to reposition and resize images",
              }}
            />
          </Typography>
        </LiItem>
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Delete any unwanted images"
              description={{
                text: "Option to delete images",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="For tips on how to best showcase your products, visit <LinkFormatting>Image Guidelines for Product Listings</LinkFormatting>."
          values={{
            LinkFormatting: ArticleLinkFormatting,
          }}
          description={{
            text: "Link to image guidelines",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(FEATURE_IMAGE_GUIDELINES_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
